import { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { apiGet } from '../lib/api';
import { ENTITY_ICONS, ENTITY_COLORS, ENTITY_LABELS } from '../lib/entityMeta';
import type { EntitySearchResult } from '../lib/entityUrls';

export interface FilterEntity {
  entity_type: string;
  entity_id: string;
  name: string;
}

export type FilterMode = 'and' | 'or';
export type FilterModes = Record<string, FilterMode>;

export const DEFAULT_FILTER_MODES: FilterModes = { tag: 'and', scope: 'or' };
export const TOGGLEABLE_TYPES = new Set(['tag', 'scope']);

interface EntityFilterPickerProps {
  selected: FilterEntity[];
  onChange: (entities: FilterEntity[]) => void;
  allowedTypes?: string[];
  placeholder?: string;
  filterModes?: FilterModes;
  onFilterModesChange?: (modes: FilterModes) => void;
}

export function EntityFilterPicker({
  selected,
  onChange,
  allowedTypes,
  placeholder = 'Filter by entity...',
  filterModes,
  onFilterModesChange,
}: EntityFilterPickerProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<EntitySearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!search.trim()) {
      setResults([]);
      setHighlightIndex(0);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await apiGet<EntitySearchResult[]>(
          `/entity-search?q=${encodeURIComponent(search.trim())}&limit=20`
        );
        setResults(data);
        setHighlightIndex(0);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // Scroll highlighted item into view
  useEffect(() => {
    const container = resultsRef.current;
    if (!container) return;
    const active = container.querySelector('[data-active="true"]') as HTMLElement | null;
    active?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  // Filter results: exclude already-selected and types not in allowedTypes
  const selectedKeys = new Set(selected.map(s => `${s.entity_type}:${s.entity_id}`));
  const filteredResults = results.filter(r => {
    if (selectedKeys.has(`${r.entity_type}:${r.entity_id}`)) return false;
    if (allowedTypes && !allowedTypes.includes(r.entity_type)) return false;
    return true;
  });

  // Group by entity type
  const grouped = new Map<string, EntitySearchResult[]>();
  for (const r of filteredResults) {
    const group = grouped.get(r.entity_type) ?? [];
    group.push(r);
    grouped.set(r.entity_type, group);
  }

  // Flat list for keyboard navigation
  const flatList = Array.from(grouped.values()).flat();

  function handleSelect(result: EntitySearchResult) {
    onChange([...selected, { entity_type: result.entity_type, entity_id: result.entity_id, name: result.name }]);
    setSearch('');
    setResults([]);
    inputRef.current?.focus();
  }

  function handleRemove(entity: FilterEntity) {
    onChange(selected.filter(s => !(s.entity_type === entity.entity_type && s.entity_id === entity.entity_id)));
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => (prev + 1) % Math.max(flatList.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => (prev + flatList.length - 1) % Math.max(flatList.length, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flatList[highlightIndex]) {
        handleSelect(flatList[highlightIndex]);
      }
    } else if (e.key === 'Backspace' && !search && selected.length > 0) {
      onChange(selected.slice(0, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setSearch('');
    }
  }

  // Group selected chips by type, preserving order of first appearance
  const typeOrder: string[] = [];
  const groupedSelected = new Map<string, FilterEntity[]>();
  for (const entity of selected) {
    if (!groupedSelected.has(entity.entity_type)) {
      typeOrder.push(entity.entity_type);
      groupedSelected.set(entity.entity_type, []);
    }
    groupedSelected.get(entity.entity_type)!.push(entity);
  }

  function renderChip(entity: FilterEntity) {
    const Icon = ENTITY_ICONS[entity.entity_type];
    const color = ENTITY_COLORS[entity.entity_type] ?? '#6366f1';
    return (
      <span
        key={`${entity.entity_type}:${entity.entity_id}`}
        className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full"
        style={{ backgroundColor: color + '20', color }}
      >
        {Icon && <Icon size={10} />}
        {entity.name}
        <button
          onClick={(e) => { e.stopPropagation(); handleRemove(entity); }}
          className="hover:opacity-70"
        >
          <X size={10} />
        </button>
      </span>
    );
  }

  function renderModePill(type: string) {
    if (!filterModes || !onFilterModesChange) return null;
    const mode = filterModes[type] ?? DEFAULT_FILTER_MODES[type] ?? 'or';
    const isAnd = mode === 'and';
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onFilterModesChange({ ...filterModes, [type]: isAnd ? 'or' : 'and' });
        }}
        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border cursor-pointer transition-colors ${
          isAnd
            ? 'bg-accent/15 border-accent text-accent'
            : 'bg-warning/15 border-warning text-warning'
        }`}
      >
        {isAnd ? 'AND' : 'OR'}
      </button>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <div
        className="flex items-center gap-1 flex-wrap px-2 py-1 bg-bg-input border border-border rounded text-sm min-w-[200px] cursor-text"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
      >
        {typeOrder.map(type => {
          const entities = groupedSelected.get(type)!;
          const showPill = TOGGLEABLE_TYPES.has(type) && entities.length >= 2 && filterModes && onFilterModesChange;
          return entities.map((entity, i) => (
            <span key={`${entity.entity_type}:${entity.entity_id}`} className="contents">
              {showPill && i === 1 && renderModePill(type)}
              {renderChip(entity)}
            </span>
          ));
        })}
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? placeholder : ''}
          className="bg-transparent border-none outline-none text-text-primary placeholder:text-text-muted text-sm min-w-[80px] flex-1"
        />
        {loading && (
          <div className="animate-spin rounded-full h-3 w-3 border border-accent border-t-transparent flex-shrink-0" />
        )}
      </div>
      {open && search.trim() && (
        <div
          ref={resultsRef}
          className="absolute z-20 top-full left-0 mt-1 w-80 max-h-64 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg"
        >
          {filteredResults.length === 0 && !loading ? (
            <div className="py-4 text-center text-xs text-text-muted">
              {results.length > 0 ? 'All results already selected' : `No results for "${search}"`}
            </div>
          ) : (
            <div className="py-1">
              {(() => {
                let flatIdx = 0;
                return Array.from(grouped.entries()).map(([type, items]) => (
                  <div key={type}>
                    <div className="px-3 py-1 text-[10px] font-semibold text-text-muted uppercase tracking-wider">
                      {ENTITY_LABELS[type] ?? type}
                    </div>
                    {items.map(item => {
                      const idx = flatIdx++;
                      const Icon = ENTITY_ICONS[item.entity_type];
                      const iconColor = ENTITY_COLORS[item.entity_type] ?? '#6366f1';
                      return (
                        <button
                          key={`${item.entity_type}-${item.entity_id}`}
                          data-active={idx === highlightIndex}
                          onClick={() => handleSelect(item)}
                          onMouseEnter={() => setHighlightIndex(idx)}
                          className={`flex items-center gap-2 w-full px-3 py-1.5 text-left text-xs transition-colors ${
                            idx === highlightIndex ? 'bg-bg-hover text-text-primary' : 'text-text-secondary hover:bg-bg-hover'
                          }`}
                        >
                          {Icon && <Icon size={14} className="flex-shrink-0" style={{ color: iconColor }} />}
                          <span className="flex-1 truncate">{item.name}</span>
                          <span className="text-[10px] text-text-muted uppercase">{ENTITY_LABELS[item.entity_type]}</span>
                        </button>
                      );
                    })}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
